

import React, { useEffect, useState } from "react";
import {
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Button,
  Modal,
  TextInput,
  TableToolbarAction,
  TableToolbarMenu,
} from "@carbon/react";
import { Add } from "@carbon/icons-react";
import { callApi } from "../../../services/apiService";

const headers = [
  { key: "country", header: "Country" },
  { key: "status", header: "Status" },
  { key: "createdBy", header: "Created By" },
  { key: "createdAt", header: "Created At" },
  { key: "actions", header: "Actions" },
];

export default function CountryMaster() {
  const [rows, setRows] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [country, setCountry] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Fetch countries
  const fetchCountries = async () => {
    try {
      const response = await callApi("GET", "/countries/");
      const formattedRows = response.data.map((item) => ({
        id: item.id,
        country: item.c_name,
        status: item.status,
        createdBy: item.created_user,
        createdAt: item.created_at,
      }));
      setRows(formattedRows);
    } catch (error) {
      console.error("Failed to fetch countries:", error.message);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  // Open modal for Add/Edit
  const openModal = (row = null) => {
    if (row) {
      setCountry(row.country);
      setSelectedRow(row);
    } else {
      setCountry("");
      setSelectedRow(null);
    }
    setIsModalOpen(true);
  };

  // Reset modal
  const resetModal = () => {
    setIsModalOpen(false);
    setCountry("");
    setSelectedRow(null);
  };

  // Save (Add / Update)
  const handleSave = async (e) => {
    e.preventDefault();
    if (!country.trim()) {
      alert("Country name is required");
      return;
    }

    const payload = {
      COUNTRY_ID: selectedRow ? selectedRow.id : 0,
      COUNTRY_NAME: country,
      COUNTRY_CODE: country.substring(0, 3).toUpperCase(),
      STATUS: "Active",
      LAST_ACTIVITY_SYSTEM_CODE: "WEB",
      ...(selectedRow
        ? { UPDATED_BY_USER_ID: 1 }
        : { CREATED_BY_USER_ID: 1 }),
    };

    try {
      if (selectedRow) {
        await callApi("PUT", "/countries/", payload);
      } else {
        await callApi("POST", "/countries/", payload);
      }

      fetchCountries();
      resetModal();
    } catch (error) {
      console.error("Failed to save country:", error.message);
    }
  };

  // Ask before delete
  const confirmDelete = (id) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  // API delete ithuku
  const handleConfirmDelete = async () => {
  try {
    await callApi("DELETE", `/countries/${deleteId}`);
    await fetchCountries(); // ✅ reloads fresh data
  } catch (error) {
    console.error("Failed to delete country:", error.message);
  } finally {
    setIsDeleteModalOpen(false);
    setDeleteId(null);
  }
};


  return (
    <div>
      <TableContainer title="Country Master">
        <TableToolbar>
          <TableToolbarContent>
            <TableToolbarSearch persistent />
            <Button renderIcon={Add} onClick={() => openModal()}>
              Add Country
            </Button>
          </TableToolbarContent>
        </TableToolbar>

        <Table>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableHeader key={header.key}>{header.header}</TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.country}</TableCell>
                <TableCell>{row.status}</TableCell>
                <TableCell>{row.createdBy}</TableCell>
                <TableCell>{row.createdAt}</TableCell>
                <TableCell>
                  <TableToolbarMenu>
                    <TableToolbarAction onClick={() => openModal(row)}>
                      Edit
                    </TableToolbarAction>
                    <TableToolbarAction onClick={() => confirmDelete(row.id)}>
                      Delete
                    </TableToolbarAction>
                  </TableToolbarMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Modal */}
      <Modal
        open={isModalOpen}
        onRequestClose={resetModal}
        modalHeading={selectedRow ? "Edit Country" : "Add Country"}
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleSave}
      >
        <TextInput
          id="country-name"
          labelText="Country Name"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          required
          invalid={!country.trim()}
          invalidText="Country name is required"
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        danger
        onRequestClose={() => setIsDeleteModalOpen(false)}
        onRequestSubmit={handleConfirmDelete}
      >
        <p>Are you sure you want to delete this country?</p>
      </Modal>
    </div>
  );
}
